import { supabase } from '@/integrations/supabase/client';
import type { PrivateImageGeneratorResponse } from '../types';

/**
 * Supabase client wrapper for private image generator operations
 * Uses direct queries for images with workbook_id = NULL
 */
export class PrivateImageGeneratorClient {
  /**
   * Fetch paginated private images using direct query with filter metadata support
   */
  static async fetchPrivateImages(
    userId: string,
    searchQuery?: string,
    limit: number = 12,
    offset: number = 0,
    projectId?: string | null,
    filters?: {
      styleId?: string;
      influenceId?: string;
      backgroundId?: string;
      environmentIds?: string[];
      moodIds?: string[];
      colorIds?: string[];
      imageType?: string;
      width?: number;
      height?: number;
    }
  ): Promise<PrivateImageGeneratorResponse[]> {
    let query = supabase
      .from('workbook_images')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    // Add project filter if provided
    if (projectId !== undefined && projectId !== null) {
      // Filter for images assigned to specific project
      query = query.eq('workbook_id', projectId);
    }
    // If projectId is null or undefined, show all images (no filtering)

    // Add search filter if provided
    if (searchQuery?.trim()) {
      query = query.or(`generation_prompt.ilike.%${searchQuery}%,alt_text.ilike.%${searchQuery}%,filename.ilike.%${searchQuery}%`);
    }

    // Add JSONB filter metadata queries - use AND logic for filters
    if (filters) {
      // Check if any filters are actually set
      const hasActiveFilters = filters.styleId || filters.influenceId || filters.backgroundId || 
                              filters.imageType || filters.width || filters.height ||
                              (filters.environmentIds && filters.environmentIds.length > 0) ||
                              (filters.moodIds && filters.moodIds.length > 0) ||
                              (filters.colorIds && filters.colorIds.length > 0);
      
      if (hasActiveFilters) {
        // Only show images that have filter_metadata when filters are active
        query = query.not('filter_metadata', 'is', null);
        
        // Single-value filters (style, influence, background, imageType)
        if (filters.styleId) {
          query = query.eq('filter_metadata->>styleId', filters.styleId);
        }
        
        if (filters.influenceId) {
          query = query.eq('filter_metadata->>influenceId', filters.influenceId);
        }
        
        if (filters.backgroundId) {
          query = query.eq('filter_metadata->>backgroundId', filters.backgroundId);
        }
        
        if (filters.imageType) {
          query = query.eq('filter_metadata->>imageType', filters.imageType);
        }
        
        // Dimension filters
        if (filters.width) {
          query = query.eq('filter_metadata->>width', filters.width.toString());
        }
        
        if (filters.height) {
          query = query.eq('filter_metadata->>height', filters.height.toString());
        }
        
        // Array filters (environment, mood, color) - check if array contains ALL selected values
        if (filters.environmentIds && filters.environmentIds.length > 0) {
          // Check that filter_metadata->environmentIds contains all the selected IDs
          filters.environmentIds.forEach(id => {
            query = query.contains('filter_metadata->environmentIds', [id]);
          });
        }
        
        if (filters.moodIds && filters.moodIds.length > 0) {
          // Check that filter_metadata->moodIds contains all the selected IDs
          filters.moodIds.forEach(id => {
            query = query.contains('filter_metadata->moodIds', [id]);
          });
        }
        
        if (filters.colorIds && filters.colorIds.length > 0) {
          // Check that filter_metadata->colorIds contains all the selected IDs
          filters.colorIds.forEach(id => {
            query = query.contains('filter_metadata->colorIds', [id]);
          });
        }
      }
    }

    // Add pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      console.error('[PrivateImageGeneratorClient] Error fetching images:', error);
      throw error;
    }

    return (data as PrivateImageGeneratorResponse[]) || [];
  }

  /**
   * Get total count of private images for a user with filter support
   */
  static async getPrivateImageCount(
    userId: string, 
    searchQuery?: string, 
    projectId?: string | null,
    filters?: {
      styleId?: string;
      influenceId?: string;
      backgroundId?: string;
      environmentIds?: string[];
      moodIds?: string[];
      colorIds?: string[];
      imageType?: string;
      width?: number;
      height?: number;
    }
  ): Promise<number> {
    let query = supabase
      .from('workbook_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('deleted_at', null);

    // Add project filter if provided
    if (projectId !== undefined && projectId !== null) {
      // Filter for images assigned to specific project
      query = query.eq('workbook_id', projectId);
    }
    // If projectId is null or undefined, show all images (no filtering)

    // Add search filter if provided
    if (searchQuery?.trim()) {
      query = query.or(`generation_prompt.ilike.%${searchQuery}%,alt_text.ilike.%${searchQuery}%,filename.ilike.%${searchQuery}%`);
    }

    // Add JSONB filter metadata queries - same as fetchPrivateImages
    if (filters) {
      // Check if any filters are actually set
      const hasActiveFilters = filters.styleId || filters.influenceId || filters.backgroundId || 
                              filters.imageType || filters.width || filters.height ||
                              (filters.environmentIds && filters.environmentIds.length > 0) ||
                              (filters.moodIds && filters.moodIds.length > 0) ||
                              (filters.colorIds && filters.colorIds.length > 0);
      
      if (hasActiveFilters) {
        // Only show images that have filter_metadata when filters are active
        query = query.not('filter_metadata', 'is', null);
        
        // Single-value filters (style, influence, background, imageType)
        if (filters.styleId) {
          query = query.eq('filter_metadata->>styleId', filters.styleId);
        }
        
        if (filters.influenceId) {
          query = query.eq('filter_metadata->>influenceId', filters.influenceId);
        }
        
        if (filters.backgroundId) {
          query = query.eq('filter_metadata->>backgroundId', filters.backgroundId);
        }
        
        if (filters.imageType) {
          query = query.eq('filter_metadata->>imageType', filters.imageType);
        }
        
        // Dimension filters
        if (filters.width) {
          query = query.eq('filter_metadata->>width', filters.width.toString());
        }
        
        if (filters.height) {
          query = query.eq('filter_metadata->>height', filters.height.toString());
        }
        
        // Array filters (environment, mood, color)
        if (filters.environmentIds && filters.environmentIds.length > 0) {
          // Check that filter_metadata->environmentIds contains all the selected IDs
          filters.environmentIds.forEach(id => {
            query = query.contains('filter_metadata->environmentIds', [id]);
          });
        }
        
        if (filters.moodIds && filters.moodIds.length > 0) {
          // Check that filter_metadata->moodIds contains all the selected IDs
          filters.moodIds.forEach(id => {
            query = query.contains('filter_metadata->moodIds', [id]);
          });
        }
        
        if (filters.colorIds && filters.colorIds.length > 0) {
          // Check that filter_metadata->colorIds contains all the selected IDs
          filters.colorIds.forEach(id => {
            query = query.contains('filter_metadata->colorIds', [id]);
          });
        }
      }
    }

    const { count, error } = await query;

    if (error) {
      console.error('[PrivateImageGeneratorClient] Error getting image count:', error);
      throw error;
    }

    return count || 0;
  }

  /**
   * Delete a private image (hard delete)
   */
  static async deletePrivateImage(imageId: string, userId: string): Promise<void> {
    console.log('[PrivateImageGeneratorClient] deletePrivateImage started:', {
      imageId: imageId,
      userId: userId,
      timestamp: new Date().toISOString()
    });
    
    // First get the image to get storage path - let RLS handle authorization
    console.log('[PrivateImageGeneratorClient] Fetching image data for deletion...');
    const { data: imageData, error: fetchError } = await supabase
      .from('workbook_images')
      .select('*')
      .eq('id', imageId)
      .single();

    console.log('[PrivateImageGeneratorClient] Fetch image result:', {
      success: !fetchError,
      hasData: !!imageData,
      fetchError: fetchError,
      imageData: imageData ? {
        id: imageData.id,
        user_id: imageData.user_id,
        filename: imageData.filename,
        storage_path: imageData.storage_path,
        workbook_id: imageData.workbook_id
      } : null
    });

    if (fetchError) {
      const error = new Error(`Failed to fetch image data: ${fetchError.message}`);
      console.error('[PrivateImageGeneratorClient] Fetch failed:', {
        imageId: imageId,
        userId: userId,
        fetchError: fetchError,
        errorCode: fetchError.code,
        errorDetails: fetchError.details,
        errorHint: fetchError.hint
      });
      throw error;
    }

    // Delete from storage
    console.log('[PrivateImageGeneratorClient] Deleting from storage:', imageData.storage_path);
    const { error: storageError } = await supabase.storage
      .from('workbook_images')
      .remove([imageData.storage_path]);

    if (storageError) {
      console.error('[PrivateImageGeneratorClient] Storage deletion error (continuing anyway):', {
        storagePath: imageData.storage_path,
        storageError: storageError,
        timestamp: new Date().toISOString()
      });
      // Continue with database deletion even if storage fails
    } else {
      console.log('[PrivateImageGeneratorClient] Storage deletion successful:', imageData.storage_path);
    }

    // Delete from database - let RLS handle authorization, don't add user_id filter
    console.log('[PrivateImageGeneratorClient] Deleting from database with conditions:', {
      imageId: imageId,
      userId: userId,
      note: 'Using only id filter, letting RLS handle authorization'
    });
    
    const { error: dbError } = await supabase
      .from('workbook_images')
      .delete()
      .eq('id', imageId);

    console.log('[PrivateImageGeneratorClient] Database deletion result:', {
      success: !dbError,
      dbError: dbError,
      imageId: imageId,
      userId: userId,
      timestamp: new Date().toISOString()
    });

    if (dbError) {
      const error = new Error(`Failed to delete image record: ${dbError.message}`);
      console.error('[PrivateImageGeneratorClient] Database deletion failed:', {
        imageId: imageId,
        userId: userId,
        dbError: dbError,
        errorCode: dbError.code,
        errorDetails: dbError.details,
        errorHint: dbError.hint
      });
      throw error;
    }
    
    console.log('[PrivateImageGeneratorClient] deletePrivateImage completed successfully for:', imageId);
  }

  /**
   * Update the project assignment (workbook_id) for a private image
   */
  static async updateImageProject(
    imageId: string, 
    userId: string, 
    projectId: string | null
  ): Promise<void> {
    // First verify the image exists and belongs to the current user
    const { data: existingImage, error: checkError } = await supabase
      .from('workbook_images')
      .select('id, user_id, workbook_id')
      .eq('id', imageId)
      .single();

    if (checkError) {
      if (checkError.code === 'PGRST116') {
        throw new Error('Image not found');
      }
      throw new Error(`Failed to verify image ownership: ${checkError.message}`);
    }

    if (!existingImage) {
      throw new Error('Image not found');
    }

    if (existingImage.user_id !== userId) {
      throw new Error('You can only modify project assignments for images you created');
    }

    // Now perform the update (we know the user owns the image)
    const { error } = await supabase
      .from('workbook_images')
      .update({ 
        workbook_id: projectId,
        updated_at: new Date().toISOString()
      })
      .eq('id', imageId)
      .eq('user_id', userId);

    if (error) {
      throw new Error(`Failed to update image project: ${error.message}`);
    }
  }

  /**
   * Check if the current user owns an image
   */
  static async checkImageOwnership(imageId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('workbook_images')
      .select('user_id')
      .eq('id', imageId)
      .single();

    if (error) {
      // If image doesn't exist or there's an error, assume no ownership
      return false;
    }

    return data.user_id === userId;
  }

  /**
   * Get the current project assignment (workbook_id) for an image
   */
  static async getImageProject(imageId: string, userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('workbook_images')
      .select('workbook_id')
      .eq('id', imageId)
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Image not found
      }
      throw new Error(`Failed to get image project: ${error.message}`);
    }

    return data.workbook_id;
  }
} 